import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

function App() {
  const [playingId, setPlayingId] = useState<string>();
  const [isPaused, setIsPaused] = useState(false);
  const [query, setQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [data, setData] = useState<ButtonData[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    fetch('/api/buttons')
      .then((response) => response.json())
      .then((json) => setData(json))
  }, [])

  const filtered = useMemo(
    () => data
      .filter(({ label }) => label.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => sortOrder === 'asc'
        ? a.label.localeCompare(b.label, 'it')
        : b.label.localeCompare(a.label, 'it')),
    [data, query, sortOrder]
  );

  const stop = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingId(undefined);
    setIsPaused(false);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPaused) {
      audio.play();
      setIsPaused(false);
    } else {
      audio.pause();
      setIsPaused(true);
    }
  };

  const play = ({ id, audio_key }: ButtonData) => {
    if (!audio_key) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = `/api/audio/${audio_key}`;
    audio.currentTime = 0;
    audio.play();
    setPlayingId(id);
    setIsPaused(false);
  };

  if (!data) return <h1>Loading...</h1>;

  return (
    <div className='min-h-screen w-full flex flex-col bg-[oklch(0.15_0.012_260)] text-[oklch(0.93_0.006_260)]'>
      <header className='px-4 sm:px-8 md:px-12 pt-7 pb-6 flex flex-col gap-[18px] border-b border-[oklch(0.28_0.015_260)]'>
        <div className='flex items-center justify-between flex-wrap gap-x-6 gap-y-2'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-xl bg-[oklch(0.78_0.16_70)] flex items-center justify-center shrink-0'>
              <div className='flex items-center'>
                <div
                  className='shrink-0'
                  style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: '8px solid oklch(0.18 0.01 70)' }}
                />
                <div className='relative w-4 h-4 flex items-center justify-center' style={{ marginLeft: -4 }}>
                  <div
                    className='absolute'
                    style={{ width: 9, height: 9, borderRadius: '50%', border: '1.6px solid oklch(0.18 0.01 70)', borderLeftColor: 'transparent', borderBottomColor: 'transparent', borderTopColor: 'transparent' }}
                  />
                  <div
                    className='absolute'
                    style={{ width: 20, height: 22, borderRadius: '50%', border: '1.6px solid oklch(0.18 0.01 70)', borderLeftColor: 'transparent', borderBottomColor: 'transparent', borderTopColor: 'transparent' }}
                  />
                </div>
              </div>
            </div>
            <div className='font-bold text-2xl tracking-tight text-[oklch(0.96_0.004_260)]' style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Soundy
            </div>
          </div>
          <div className='flex items-center gap-2 sm:gap-4'>
            <Link
              to='/manage'
              className='text-[10px] sm:text-xs tracking-wide uppercase text-[oklch(0.55_0.01_260)] whitespace-nowrap no-underline hover:text-[oklch(0.7_0.01_260)] border border-[oklch(0.32_0.016_260)] rounded-md px-2 sm:px-2.5 py-1.5'
            >
              Gestisci suoni
            </Link>
            <a
              href='https://vas.it'
              target='_blank'
              rel='noreferrer'
              className='text-[10px] sm:text-xs tracking-wide uppercase text-[oklch(0.5_0.01_260)] whitespace-nowrap no-underline hover:text-[oklch(0.62_0.01_260)]'
            >
              Powered by <span className='text-[oklch(0.72_0.02_260)] font-semibold'>VAS</span>
            </a>
          </div>
        </div>
        <div>
          <h1 className='font-semibold text-[19px] tracking-tight text-[oklch(0.9_0.006_260)]' style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Un suono per ogni occasione
          </h1>
          <p className='text-sm text-[oklch(0.6_0.01_260)] mt-[3px]'>
            Tocca un pulsante per riprodurre. Un solo suono alla volta.
          </p>
        </div>
        <div className='flex items-center gap-2.5 max-w-md'>
          <div className='relative flex-1'>
            <input
              type='text'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Cerca un suono per nome…'
              className='w-full box-border bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-3 pl-10 pr-4 text-[15px] text-[oklch(0.93_0.006_260)] outline-none focus:border-[oklch(0.7_0.15_70)]'
            />
            <span className='absolute left-3.5 top-1/2 -translate-y-1/2 text-[oklch(0.55_0.01_260)] text-base'>⌕</span>
          </div>
          <button
            type='button'
            onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
            title={sortOrder === 'asc' ? 'Ordine A→Z (clicca per Z→A)' : 'Ordine Z→A (clicca per A→Z)'}
            className='shrink-0 flex items-center gap-1.5 bg-[oklch(0.2_0.014_260)] border border-[oklch(0.3_0.016_260)] rounded-[10px] py-3 px-3.5 text-[15px] text-[oklch(0.93_0.006_260)] hover:border-[oklch(0.42_0.02_260)] cursor-pointer'
          >
            <span aria-hidden='true'>{sortOrder === 'asc' ? 'A→Z' : 'Z→A'}</span>
          </button>
        </div>
      </header>

      <main className='flex-1 px-4 sm:px-8 md:px-12 py-8'>
        {filtered.length > 0 ? (
          <div className='grid gap-[18px]' style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {filtered.map((sound) => (
              <SoundCard
                key={sound.id}
                label={sound.label}
                isReady={!!sound.audio_key}
                isActive={playingId === sound.id}
                isPlaying={playingId === sound.id && !isPaused}
                onClick={() => (playingId === sound.id ? togglePlayPause() : play(sound))}
                onTogglePlayPause={togglePlayPause}
                onStop={stop}
              />
            ))}
          </div>
        ) : (
          <div className='text-[oklch(0.55_0.01_260)] text-[15px] py-10 text-center'>
            Nessun suono trovato per “{query}”.
          </div>
        )}
      </main>

      <audio ref={audioRef} onEnded={stop} />
    </div>
  );
}

export default App;

type ButtonData = {
  id: string;
  label: string;
  youtube_id: string;
  from_seconds: number;
  to_seconds: number;
  color: string | null;
  audio_key: string | null;
}

const SoundCard: React.FC<{
  label: string;
  isReady: boolean;
  isActive: boolean;
  isPlaying: boolean;
  onClick: () => void;
  onTogglePlayPause: () => void;
  onStop: () => void;
}> = ({ label, isReady, isActive, isPlaying, onClick, onTogglePlayPause, onStop }) => (
  <button
    onClick={onClick}
    disabled={!isReady}
    className={clsx(
      'relative text-left border rounded-2xl px-[26px] py-[26px] select-none transition-colors',
      !isReady && 'opacity-40 cursor-not-allowed',
      isReady && 'cursor-pointer',
      isActive
        ? 'border-[oklch(0.75_0.16_70)] bg-[oklch(0.23_0.02_260)] animate-[sb-pulse_1.8s_ease-in-out_infinite]'
        : 'border-[oklch(0.3_0.016_260)] bg-[oklch(0.2_0.014_260)] hover:border-[oklch(0.42_0.02_260)]'
    )}
  >
    <span
      className={clsx(
        'block font-semibold text-base',
        isActive ? 'text-[oklch(0.97_0.01_70)]' : 'text-[oklch(0.9_0.006_260)]'
      )}
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {label}
    </span>

    {isActive && (
      <div className='flex items-center gap-2.5 mt-3.5'>
        <div className='flex items-center gap-[3px] h-[18px]' aria-hidden='true'>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={clsx(
                'w-[3px] rounded-[2px] bg-[oklch(0.8_0.15_70)] transition-[height]',
                isPlaying ? 'animate-[sb-bar_0.9s_ease-in-out_infinite]' : 'h-[30%]'
              )}
              style={isPlaying ? { animationDelay: `${i * 0.12}s` } : undefined}
            />
          ))}
        </div>
        <div className='flex-1' />
        <span
          onClick={(e) => { e.stopPropagation(); onTogglePlayPause(); }}
          className='w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[oklch(0.75_0.16_70)] text-[oklch(0.18_0.01_70)] text-xs cursor-pointer'
        >
          {isPlaying ? '❙❙' : '▶'}
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); onStop(); }}
          className='w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[oklch(0.28_0.02_30)] text-[oklch(0.75_0.13_30)] text-[11px] cursor-pointer border border-[oklch(0.4_0.03_30)]'
        >
          ◼
        </span>
      </div>
    )}
  </button>
)
